import React, { useEffect, useState } from "react";
import client, { apiErr } from "../../lib/api";
import { Panel, Spinner } from "../ui-kit";
import { Plus, StickyNote, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Drawer, Field, Empty, inputCls, fmtDate } from "./shared";

export const STAGES = ["New Lead", "Contacted", "Qualified", "Free Community", "Interested", "Broker Link Sent", "Account Application", "Verification Pending", "Broker Verified", "Premium Activated", "Renewal Due"];

function AddLead({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", country: "", interest: "Both" });
  const save = async () => {
    try { await client.post("/tm/leads", f); toast.success("Lead added"); onSaved(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Drawer title="Add Lead" onClose={onClose} testid="lead-drawer">
      <div className="space-y-3">
        {["name", "phone", "email", "country"].map((k) => (
          <Field key={k} label={k}><input data-testid={`lead-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} /></Field>
        ))}
        <Field label="Interest">
          <select data-testid="lead-interest" value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })} className={inputCls}>
            {["Both", "Gold", "Bitcoin", "Algo"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <button data-testid="lead-save-btn" onClick={save} className="btn-gold w-full py-2.5 text-sm">Save Lead</button>
      </div>
    </Drawer>
  );
}

function LeadDetail({ lead, onClose, onChange }) {
  const [note, setNote] = useState("");
  const [fu, setFu] = useState(lead.next_followup ? lead.next_followup.slice(0, 16) : "");
  const patch = async (body) => {
    try { const { data } = await client.patch(`/tm/leads/${lead.id}`, body); onChange(data); toast.success("Updated"); } catch (e) { toast.error(apiErr(e)); }
  };
  const addNote = async () => {
    try { const { data } = await client.post(`/tm/leads/${lead.id}/note`, { text: note }); setNote(""); onChange(data); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Drawer title={lead.name} onClose={onClose} testid="lead-detail">
      <div className="text-sm text-slate-400 mb-4">{lead.phone} {lead.email && `· ${lead.email}`}{lead.capital ? ` · Capital ${lead.capital}` : ""}</div>
      <div className="space-y-3 mb-5">
        <Field label="Stage">
          <select data-testid="lead-stage-select" value={STAGES.includes(lead.status) ? lead.status : "New Lead"} onChange={(e) => patch({ status: e.target.value })} className={inputCls}>
            {STAGES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Next follow-up">
          <div className="flex gap-2">
            <input data-testid="lead-followup-input" type="datetime-local" value={fu} onChange={(e) => setFu(e.target.value)} className={inputCls} />
            <button data-testid="lead-followup-save" onClick={() => fu && patch({ next_followup: new Date(fu).toISOString() })} className="btn-gold px-3 text-xs">Set</button>
          </div>
        </Field>
        <Field label="Add note">
          <div className="flex gap-2">
            <input data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="Call kiya, interested hai…" />
            <button data-testid="lead-note-save" onClick={addNote} disabled={!note.trim()} className="btn-gold px-3 text-xs disabled:opacity-50">Add</button>
          </div>
        </Field>
      </div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Timeline</div>
      {(lead.activities || []).slice().reverse().map((a, i) => (
        <div key={i} className="border-l-2 border-gold/40 pl-3 py-1.5 mb-1 text-sm">
          <div className="text-slate-200">{a.text}</div>
          <div className="text-[11px] text-slate-500">{a.by} · {fmtDate(a.at)}</div>
        </div>
      ))}
    </Drawer>
  );
}

export default function TeamLeads() {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [sel, setSel] = useState(null);
  const load = () => client.get("/tm/leads").then(({ data }) => setRows(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  const due = (rows || []).filter((l) => l.next_followup && new Date(l.next_followup) <= new Date());
  const onChange = (l) => { setSel(l); setRows((rs) => rs.map((x) => (x.id === l.id ? l : x))); };
  return (
    <Panel testid="tm-leads-panel" title={`My Leads ${rows ? `(${rows.length})` : ""}${due.length ? ` · ${due.length} follow-ups due` : ""}`}
      right={<button data-testid="add-lead-btn" onClick={() => setAdding(true)} className="btn-gold px-3 py-1.5 text-xs flex items-center gap-1.5"><Plus size={14} />Add Lead</button>}>
      {!rows ? <Spinner /> : rows.length === 0 ? <Empty testid="leads-empty" text="Koi lead nahi hai. Admin leads assign karega ya aap khud add karein." /> :
        rows.map((l) => {
          const isDue = l.next_followup && new Date(l.next_followup) <= new Date();
          return (
            <button key={l.id} data-testid={`tm-lead-${l.id}`} onClick={() => setSel(l)} className="w-full text-left flex items-center gap-3 px-4 py-3 border-b border-[color:var(--border-subtle)] last:border-0 hover:bg-white/5 transition-colors duration-200">
              <div className="min-w-0">
                <div className="text-white truncate">{l.name}</div>
                <div className="text-xs text-slate-500">{l.phone} · {l.source}{l.capital ? ` · Capital ${l.capital}` : ""}</div>
                {l.email && <div className="text-[11px] text-slate-600 truncate">{l.email}</div>}
              </div>
              <span className="ml-auto text-[11px] px-2 py-0.5 border border-[color:var(--border-strong)] text-slate-300 whitespace-nowrap">{l.status}</span>
              {l.next_followup && <span className={`text-[11px] flex items-center gap-1 whitespace-nowrap ${isDue ? "text-bear" : "text-slate-500"}`}><CalendarClock size={12} />{fmtDate(l.next_followup)}</span>}
              {(l.activities || []).some((a) => a.type === "note") && <StickyNote size={13} className="text-gold" />}
            </button>
          );
        })}
      {adding && <AddLead onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {sel && <LeadDetail lead={sel} onClose={() => setSel(null)} onChange={onChange} />}
    </Panel>
  );
}
